import { type TypedDocumentNode } from '@graphql-typed-document-node/core'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { useGraphQLRequest } from '~/hooks/useGraphQLRequest'
import { useNotify } from '~/hooks/useNotify'
import { useOpenUrlInNewTab } from '~/hooks/useOpenUrlInNewTab'
import { exists } from '~/utils'

import { useSurveyId } from './useSurveyId'

type Result = {
  generateDownloadURL?: {
    url: string
  } | null
}
type Variables = {
  surveyId: string
}

export const useDownloadFile = <TResult extends Result>(
  document: TypedDocumentNode<TResult, Variables>,
) => {
  const graphQLRequest = useGraphQLRequest()
  const surveyId = useSurveyId()
  const notify = useNotify()
  const openUrlInNewTab = useOpenUrlInNewTab()
  const { t } = useTranslation()

  return React.useCallback(async () => {
    const result = await graphQLRequest({ document, variables: { surveyId } })

    const url = result.generateDownloadURL?.url
    if (!exists(url)) {
      notify({
        content: t('could_not_get_download_url'),
        style: 'error',
      })

      return
    }
    openUrlInNewTab(url)
  }, [document, graphQLRequest, notify, openUrlInNewTab, surveyId, t])
}
